import type { CellClassParams, ColDef, ColGroupDef } from 'ag-grid-community';
import GAgGrid from 'components/technical/grids/GAgGrid';
import { Suspense, useMemo, useState } from 'react';
import isNil from 'lodash/fp/isNil';
import { Stack } from '@mui/joy';
import HeaderBar from 'components/technical/HeaderBar/HeaderBar.tsx';
import Loader from 'components/technical/Loader/Loader.tsx';
import { createColumnToolDef } from 'components/technical/grids/agGrid.utils';
import type { ISubFundPnlQuery } from 'generated/graphql';
import { TabList, Tabs, Tab } from '@mui/joy';

interface ReturnRow {
  year: number;
  month?: number;
  monthName?: string;
  quarter?: number;
  quarterName?: string;
  date: UtcDate;
  // Dynamic fields for each subfund and metric type
  // biome-ignore lint/suspicious/noExplicitAny:
  [key: string]: any;
}

type Query = ISubFundPnlQuery['portfolio']['subFundsTimeWeightedReturns']['subFunds'];

const percentageColumnType = ['numericColumn', 'percentageColumn'];
const cashColumnType = ['numericColumn', 'cashColumn'];
const numericColumnType = ['numericColumn', 'extendedNumericColumn'];

const MIN_TABLE_HEIGHT = 400;
const MAX_TABLE_HEIGHT = 800; // Maximum table height
const ROW_HEIGHT = 55; // Estimated row height

const getMonthName = (monthNumber: number): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return months[monthNumber - 1] || '';
};

const getQuarterName = (quarterNumber: number): string => {
  return `Q${quarterNumber}`;
};

// biome-ignore lint/suspicious/noExplicitAny:
const positiveNegativeCellStyle = (params: CellClassParams<ReturnRow, any>) => {
  if (isNil(params.value)) {
    return null;
  }

  return {
    color: params.value < 0 ? 'var(--joy-palette-danger-plainColor)' : 'var(--joy-palette-success-plainColor)',
  };
};

export function SubFundPnlReturnsGrid(props: { data: Query }) {
  const [selectedTab, setSelectedTab] = useState(1);
  return (
    <Stack mt={4}>
      <Suspense fallback={<Loader />}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <HeaderBar title={'Subfunds Returns'} />
          <Tabs defaultValue={selectedTab} onChange={(_e, value) => setSelectedTab(value as number)}>
            <TabList>
              <Tab value={1}>Monthly</Tab>
              <Tab value={2}>Quarterly</Tab>
            </TabList>
          </Tabs>
        </Stack>

        <SubFundPnlReturnsGridContainer {...props} type={selectedTab === 1 ? 'monthly' : 'quarterly'} />
      </Suspense>
    </Stack>
  );
}

export function SubFundPnlReturnsGridContainer({ data, type }: { data: Query; type: 'monthly' | 'quarterly' }) {
  // Transform the data into rows for the grid
  const rows = useMemo((): ReturnRow[] => {
    // First, collect all unique time periods
    const timePeriodsMap = new Map<
      string,
      {
        year: number;
        month?: number;
        monthName?: string;
        quarter?: number;
        quarterName?: string;
        date: UtcDate;
        subfundData: Map<
          number,
          {
            referencePercentage: number;
            referenceAbsolute: number;
            usdPercentage: number;
            usdAbsolute: number;
          }
        >;
      }
    >();

    data.forEach((dataItem, dataIndex) => {
      const referenceReturns =
        type === 'monthly'
          ? dataItem.referenceAssetReturns.monthlyReturns
          : dataItem.referenceAssetReturns.quarterlyReturns;
      const usdReturns = type === 'monthly' ? dataItem.usdReturns.monthlyReturns : dataItem.usdReturns.quarterlyReturns;

      // Create a map for USD returns for easy lookup
      const usdReturnsMap = new Map();
      for (const usdReturn of usdReturns) {
        const key =
          type === 'monthly'
            ? `${usdReturn.year}-${'month' in usdReturn ? usdReturn.month : ''}`
            : `${usdReturn.year}-${'quarter' in usdReturn ? usdReturn.quarter : ''}`;
        usdReturnsMap.set(key, usdReturn);
      }

      for (const refReturn of referenceReturns) {
        const key =
          type === 'monthly'
            ? `${refReturn.year}-${'month' in refReturn ? refReturn.month : ''}`
            : `${refReturn.year}-${'quarter' in refReturn ? refReturn.quarter : ''}`;
        const correspondingUsdReturn = usdReturnsMap.get(key);

        if (correspondingUsdReturn) {
          const timePeriodKey = key;

          if (!timePeriodsMap.has(timePeriodKey)) {
            const timePeriodData = {
              year: refReturn.year,
              date: refReturn.date,
              subfundData: new Map(),
              // biome-ignore lint/suspicious/noExplicitAny:
            } as any;

            if (type === 'monthly' && 'month' in refReturn) {
              timePeriodData.month = refReturn.month;
              timePeriodData.monthName = getMonthName(refReturn.month);
            } else if (type === 'quarterly' && 'quarter' in refReturn) {
              timePeriodData.quarter = refReturn.quarter;
              timePeriodData.quarterName = getQuarterName(refReturn.quarter);
            }

            timePeriodsMap.set(timePeriodKey, timePeriodData);
          }

          const timePeriod = timePeriodsMap.get(timePeriodKey)!;
          timePeriod.subfundData.set(dataIndex, {
            referencePercentage: refReturn.percentage,
            referenceAbsolute: refReturn.absolute,
            usdPercentage: correspondingUsdReturn.percentage,
            usdAbsolute: correspondingUsdReturn.absolute,
          });
        }
      }
    });

    // Convert to rows where each row is a time period
    const transformedRows: ReturnRow[] = [];

    // Sort time periods by year and month/quarter
    const sortedTimePeriods = Array.from(timePeriodsMap.entries()).sort((a, b) => {
      const [_keyA, periodA] = a;
      const [_keyB, periodB] = b;
      if (periodA.year !== periodB.year) return periodA.year - periodB.year;

      if (type === 'monthly') {
        return (periodA.month || 0) - (periodB.month || 0);
      }
      return (periodA.quarter || 0) - (periodB.quarter || 0);
    });

    for (const [_key, timePeriod] of sortedTimePeriods) {
      const row: ReturnRow = {
        year: timePeriod.year,
        date: timePeriod.date,
      };

      if (type === 'monthly') {
        row.month = timePeriod.month;
        row.monthName = timePeriod.monthName;
      } else {
        row.quarter = timePeriod.quarter;
        row.quarterName = timePeriod.quarterName;
      }

      // Add data for each subfund
      for (const [subfundIndex, data] of timePeriod.subfundData) {
        row[`subfund${subfundIndex + 1}_refPercentage`] = data.referencePercentage;
        row[`subfund${subfundIndex + 1}_refAbsolute`] = data.referenceAbsolute;
        row[`subfund${subfundIndex + 1}_usdPercentage`] = data.usdPercentage;
        row[`subfund${subfundIndex + 1}_usdAbsolute`] = data.usdAbsolute;
      }

      transformedRows.push(row);
    }

    return transformedRows;
  }, [data, type]);

  // Define column structure: Time | Ref % | Ref Abs | USD % | USD Abs
  const columnDefs = useMemo((): (ColDef<ReturnRow> | ColGroupDef<ReturnRow>)[] => {
    const columns: (ColDef<ReturnRow> | ColGroupDef<ReturnRow>)[] = [];

    // Get number of subfunds
    const numberOfSubfunds = data.length;

    // If no data, return empty column definitions
    if (numberOfSubfunds === 0) {
      return [];
    }

    // Time group columns
    const timeGroupChildren = [
      {
        headerName: 'Year',
        field: 'year',
        width: 80,
        cellStyle: { fontWeight: 'bold' },
      },
      {
        headerName: type === 'monthly' ? 'Month' : 'Quarter',
        field: type === 'monthly' ? 'monthName' : 'quarterName',
        width: 100,
        cellStyle: { fontWeight: 'bold' },
      },
      {
        headerName: 'Date',
        field: 'date',
        width: 120,
        cellStyle: { fontWeight: 'bold' },
      },
    ];

    const timeGroup: ColGroupDef<ReturnRow> = {
      headerName: 'Time',
      marryChildren: true,
      children: timeGroupChildren,
    };
    columns.push(timeGroup);

    // Create metric groups: Ref %, Ref Abs, USD %, USD Abs
    const metrics = [
      { name: 'P&L ($)', field: 'usdAbsolute' },
      { name: 'P&L', field: 'refAbsolute' },
      { name: 'P&L ($) [%]', field: 'usdPercentage' },
      { name: 'P&L [%]', field: 'refPercentage' },
    ];

    for (const metric of metrics) {
      const metricGroup: ColGroupDef<ReturnRow> = {
        headerName: metric.name,
        marryChildren: true,
        children: [],
      };

      // Create columns for each subfund under this metric
      for (let i = 1; i <= numberOfSubfunds; i++) {
        // Determine column type based on whether it's a percentage or absolute value
        const isPercentage = metric.field.includes('Percentage');
        const isRefAbsolute = metric.field === 'refAbsolute';
        const columnType = isPercentage ? percentageColumnType : isRefAbsolute ? numericColumnType : cashColumnType;

        metricGroup.children!.push({
          headerName: data[i - 1].subFund.name,
          field: `subfund${i}_${metric.field}`,
          type: columnType,
          width: 300,
          cellStyle: positiveNegativeCellStyle,
        });
      }

      columns.push(metricGroup);
    }

    return columns;
  }, [data, type]);

  // Calculate dynamic height based on the number of rows
  const dynamicHeight = useMemo(() => {
    const calculatedHeight = rows.length * ROW_HEIGHT;
    const minHeight = calculatedHeight > MIN_TABLE_HEIGHT ? calculatedHeight : MIN_TABLE_HEIGHT;
    return Math.min(minHeight, MAX_TABLE_HEIGHT);
  }, [rows.length]);

  return (
    <GAgGrid<ReturnRow>
      rowData={rows}
      height={`${dynamicHeight}px`}
      columnDefs={columnDefs}
      enableCharts
      groupDefaultExpanded={-1}
      sideBar={{
        toolPanels: [
          createColumnToolDef({
            suppressPivotMode: true,
          }),
          'filters',
        ],
      }}
      cellSelection
      defaultColDef={{
        sortable: true,
        resizable: true,
        filter: true,
      }}
    />
  );
}
